import React from "react";
import { Table } from "reactstrap";
import * as S from "../../constants/StringConstants";
import FinalTable, { FinalTableBody, FinalTableHeader, IFinalTableProps } from "../FinalTable/FinalTable";

import { Icon } from "@mui/material";
import FormTableBody from "../FinalTable/FinalFormTableBody";
import "./Table.scss";

const buildPagesList = (page: number, numPages: number, displayMax: number = 10) => {
  let a = page - displayMax / 2;
  let b = page + displayMax / 2;
  while (a < 0) {
    a++;
    b++;
  }
  while (b > numPages) {
    a--;
    b--;
  }
  let arr = [];
  for (let i = a; i <= b; i++) {
    if (i > 0 && i <= numPages) {
      arr.push(i);
    }
  }
  return arr;
};

export interface IDisTableProps<R> extends IFinalTableProps<R> {
  isPaginated: boolean;
  getRef?: any;
  showAllOptionInPaginationDropdown?: boolean;
  headerView?: React.ReactNode;
  useFormBody?: boolean;
  hidePaginationInfo?: boolean;
  noResultsMessage?: React.ReactNode;
}

export default class DisTable<R> extends React.Component<IDisTableProps<R>> {
  public static defaultProps = {
    isPaginated: true,
    showAllOptionInPaginationDropdown: false,
  };
  render() {
    const { isPaginated, children, getRef, showAllOptionInPaginationDropdown, useFormBody, noResultsMessage, ...tableProps } = this.props;

    return (
      <FinalTable<R> {...tableProps} ref={getRef}>
        {props => {
          const { page, rows, total } = props;
          const rowStart = (page - 1) * rows + 1;
          const rowEnd = Math.min(page * rows, total);
          const numPages = Math.ceil(total / rows);

          const pagesList = buildPagesList(page, numPages);
          return (
            <>
              {this.props.headerView ? this.props.headerView : null}
              <div className={`table-container-v2 ${children ? "" : "no-toolbar"}`}>
                {children && (
                  <div className="table-toolbar">
                    <div className="table-toolbar__wrap">{children}</div>
                  </div>
                )}
                <div className="table-view__main-wrap">
                  <div className="table-view__main-wrap__inner">
                    <Table className="table-view__wrap">
                      <FinalTableHeader />
                      {total === 0 && noResultsMessage ? (
                        <div className="no-results-message">
                          {noResultsMessage}
                        </div>
                      ) : (
                        useFormBody ? <FormTableBody /> : <FinalTableBody />
                      )}
                    </Table>
                  </div>
                </div>
                {isPaginated && (
                  <section className="table-footer">
                    <div className="table-footer__container">
                      {!this.props.hidePaginationInfo && (
                        <>
                          <ul className="table-footer__rows">
                            <li>{S.SHOWING_N_ENTRIES(rowStart, rowEnd, props.total)}</li>
                          </ul>
                        </>
                      )}
                      <div className="table-footer__pagination-page">
                        <div
                          className={rowStart <= 1 ? "disabled" : ""}
                          onClick={() => {
                            if (!props.loading && rowStart > 1) {
                              props.update(state => ({
                                page: 1,
                                rows: state.rows,
                                query: state.query,
                              }));
                            }
                          }}>
                          <Icon>keyboard_double_arrow_left</Icon>
                        </div>
                        <div
                          className={rowStart <= 1 ? "disabled" : ""}
                          onClick={() => {
                            if (!props.loading && rowStart > 1) {
                              props.update(state => ({
                                page: state.page - 1,
                                rows: state.rows,
                                query: state.query,
                              }));
                            }
                          }}>
                          <Icon>chevron_left</Icon>
                        </div>
                        {pagesList.map(p => (
                          <div
                            key={p}
                            className={`${p === page ? "active" : ""}`}
                            onClick={() => {
                              if (!props.loading) {
                                props.update(state => ({
                                  page: p,
                                  rows: state.rows,
                                  query: state.query,
                                }));
                              }
                            }}>
                            {p === page ? <strong>{p}</strong> : p}
                          </div>
                        ))}
                        <div
                          className={rowEnd >= props.total ? "disabled" : ""}
                          onClick={() => {
                            if (!props.loading && rowEnd < props.total) {
                              props.update(state => ({
                                page: state.page + 1,
                                rows: state.rows,
                                query: state.query,
                              }));
                            }
                          }}>
                          <Icon>chevron_right</Icon>
                        </div>
                        <div
                          className={rowEnd >= props.total ? "disabled" : ""}
                          onClick={() => {
                            if (!props.loading && rowEnd < props.total) {
                              props.update(state => ({
                                page: numPages,
                                rows: state.rows,
                                query: state.query,
                              }));
                            }
                          }}>
                          <Icon>keyboard_double_arrow_right</Icon>
                        </div>
                      </div>
                      {!this.props.hidePaginationInfo && (
                        <select
                          className="table-footer__pagination-select"
                          value={props.rows}
                          disabled={props.loading}
                          onChange={event =>
                            props.update(state => ({
                              page: 1,
                              rows: parseInt(event.target.value, 10),
                              query: state.query,
                            }))
                          }>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                          {showAllOptionInPaginationDropdown && <option value={total}>{S.PAGINATION_SHOW_ALL}</option>}
                        </select>
                      )}
                    </div>
                  </section>
                )}
              </div>
            </>
          );
        }}
      </FinalTable>
    );
  }
}
